import {
  AddOrderItemRequest,
  BulkUpdateItemFieldsRequest,
  ItemStatus,
  MarkAsShippedRequest,
  OrderItemDetail,
  OrderItemFlat,
  OrderItemFlatFilters,
  OrderItemHistoryEntry,
  PaginatedHistoryResponse,
  PaginatedOrderItemsResponse,
  UpdateOrderItemRequest,
} from "../types/admin";
import { ApiResponse } from "../types/common";
import { normalizeOrderFinancialFields } from "../utils/orderFinancials";

import apiClient, { withLongTimeout } from "./apiClient";

export interface AdminOrderItemService {
  getOrderItems(filters: OrderItemFlatFilters): Promise<PaginatedOrderItemsResponse>;
  getOrderItemDetail(orderItemId: string): Promise<OrderItemDetail>;
  getOrderItemsByOrderId(orderId: string): Promise<OrderItemDetail[]>;
  getOrderSummary(orderId: string): Promise<any>; // OrderSummaryDto
  updateOrderItem(request: UpdateOrderItemRequest): Promise<boolean>;
  updateItemStatus(
    orderItemId: string,
    newStatusId: number,
    notes?: string,
    sendNotification?: boolean
  ): Promise<boolean>;
  addTrackingNumber(
    orderItemId: string,
    trackingNumber: string
  ): Promise<boolean>;
  markAsOrdered(orderItemId: string, notes?: string): Promise<boolean>;
  markAsShipped(
    orderItemId: string,
    request: MarkAsShippedRequest
  ): Promise<boolean>;
  getAvailableItemStatuses(): Promise<ItemStatus[]>;
  getNextAllowedStatuses(currentStatusId: number): Promise<ItemStatus[]>;
  validateStatusTransition(
    currentStatusId: number,
    newStatusId: number
  ): Promise<boolean>;
  getOrderItemHistory(
    orderItemId: string,
    pageNumber?: number,
    pageSize?: number
  ): Promise<PaginatedHistoryResponse>;
  bulkUpdateItemStatus(request: {
    orderItemIds: string[];
    newItemStatusId: number;
    notes?: string;
    sendNotification?: boolean;
  }): Promise<boolean>;
  bulkUpdateItemFields(
    request: BulkUpdateItemFieldsRequest
  ): Promise<boolean>;
  addOrderItem(request: AddOrderItemRequest): Promise<OrderItemDetail>;
  deleteOrderItem(orderItemId: string): Promise<boolean>;
  restoreOrderItem(orderItemId: string): Promise<boolean>;
}

class AdminOrderItemServiceImpl implements AdminOrderItemService {
  private readonly baseUrl = "admin/order-items";

  async getOrderItems(filters: OrderItemFlatFilters): Promise<PaginatedOrderItemsResponse> {
    const params = new URLSearchParams();
    if (filters.pageNumber) params.append("pageNumber", filters.pageNumber.toString());
    if (filters.pageSize) params.append("pageSize", filters.pageSize.toString());
    if (filters.search) params.append("search", filters.search);
    if (filters.itemStatusId) params.append("itemStatusId", filters.itemStatusId.toString());
    if (filters.retailer) params.append("retailer", filters.retailer);
    if (filters.hasRetailerOrderId !== undefined) params.append("hasRetailerOrderId", filters.hasRetailerOrderId.toString());
    if (filters.hasRetailerTrackingId !== undefined) params.append("hasRetailerTrackingId", filters.hasRetailerTrackingId.toString());
    if (filters.retailerOrderId) params.append("retailerOrderId", filters.retailerOrderId);
    if (filters.retailerTrackingId) params.append("retailerTrackingId", filters.retailerTrackingId);
    if (filters.flightNumber) params.append("flightNumber", filters.flightNumber);
    if (filters.hasFlightNumber !== undefined) params.append("hasFlightNumber", filters.hasFlightNumber.toString());
    if (filters.pickUpLocation) params.append("pickUpLocation", filters.pickUpLocation);
    if (filters.includeDeleted !== undefined) params.append("includeDeleted", filters.includeDeleted.toString());
    if (filters.sortBy) params.append("sortBy", filters.sortBy);
    if (filters.sortDescending !== undefined) params.append("sortDescending", filters.sortDescending.toString());

    const response = await apiClient.get<ApiResponse<PaginatedOrderItemsResponse>>(
      `${this.baseUrl}?${params.toString()}`
    );
    if (!response.data.data) {
      throw new Error("No data received");
    }
    return response.data.data;
  }

  async getOrderItemDetail(orderItemId: string): Promise<OrderItemDetail> {
    try {
      const response = await apiClient.get<ApiResponse<OrderItemDetail>>(
        `${this.baseUrl}/${orderItemId}`
      );

      if (!response.data.data) {
        throw new Error("No data received from API");
      }

      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  async getOrderItemsByOrderId(orderId: string): Promise<OrderItemDetail[]> {
    try {
      const response = await apiClient.get<ApiResponse<OrderItemDetail[]>>(
        `${this.baseUrl}/order/${orderId}/items`
      );

      if (!response.data.data) {
        return [];
      }

      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  async getOrderSummary(orderId: string): Promise<any> {
    try {
      const response = await apiClient.get<ApiResponse<any>>(
        `${this.baseUrl}/order/${orderId}/summary`
      );
      return normalizeOrderFinancialFields(response.data.data);
    } catch (error) {
      throw error;
    }
  }

  async updateOrderItem(request: UpdateOrderItemRequest): Promise<boolean> {
    try {
      const response = await apiClient.put<ApiResponse<boolean>>(
        `${this.baseUrl}/update`,
        request
      );

      return response.data.data ?? false;
    } catch (error) {
      throw error;
    }
  }

  async updateItemStatus(
    orderItemId: string,
    newStatusId: number,
    notes?: string,
    sendNotification = true
  ): Promise<boolean> {
    try {
      const params = new URLSearchParams();
      if (notes) params.append("notes", notes);
      params.append("sendNotification", sendNotification.toString());

      const response = await apiClient.put<ApiResponse<boolean>>(
        `${this.baseUrl}/${orderItemId}/status/${newStatusId}?${params.toString()}`
      );

      return response.data.data ?? false;
    } catch (error) {
      throw error;
    }
  }

  async addTrackingNumber(
    orderItemId: string,
    trackingNumber: string
  ): Promise<boolean> {
    try {
      const response = await apiClient.put<ApiResponse<boolean>>(
        `${this.baseUrl}/${orderItemId}/tracking`,
        trackingNumber
      );

      return response.data.data ?? false;
    } catch (error) {
      throw error;
    }
  }

  async markAsOrdered(orderItemId: string, notes?: string): Promise<boolean> {
    try {
      const params = new URLSearchParams();
      if (notes) params.append("notes", notes);

      const response = await apiClient.put<ApiResponse<boolean>>(
        `${this.baseUrl}/${orderItemId}/mark-ordered?${params.toString()}`
      );

      return response.data.data ?? false;
    } catch (error) {
      throw error;
    }
  }

  async markAsShipped(
    orderItemId: string,
    request: MarkAsShippedRequest
  ): Promise<boolean> {
    try {
      const response = await apiClient.put<ApiResponse<boolean>>(
        `${this.baseUrl}/${orderItemId}/mark-shipped`,
        request
      );

      return response.data.data ?? false;
    } catch (error) {
      throw error;
    }
  }

  async getAvailableItemStatuses(): Promise<ItemStatus[]> {
    try {
      const response = await apiClient.get<ApiResponse<ItemStatus[]>>(
        `${this.baseUrl}/statuses`
      );

      if (!response.data.data) {
        return [];
      }

      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  async getNextAllowedStatuses(currentStatusId: number): Promise<ItemStatus[]> {
    try {
      const response = await apiClient.get<ApiResponse<ItemStatus[]>>(
        `${this.baseUrl}/statuses/${currentStatusId}/next`
      );

      if (!response.data.data) {
        return [];
      }

      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  async validateStatusTransition(
    currentStatusId: number,
    newStatusId: number
  ): Promise<boolean> {
    try {
      const params = new URLSearchParams({
        currentStatusId: currentStatusId.toString(),
        newStatusId: newStatusId.toString(),
      });

      const response = await apiClient.get<ApiResponse<boolean>>(
        `${this.baseUrl}/statuses/validate-transition?${params.toString()}`
      );

      return response.data.data ?? false;
    } catch (error) {
      throw error;
    }
  }

  async bulkUpdateItemStatus(request: {
    orderItemIds: string[];
    newItemStatusId: number;
    notes?: string;
    sendNotification?: boolean;
  }): Promise<boolean> {
    try {
      const response = await apiClient.put<ApiResponse<boolean>>(
        `${this.baseUrl}/bulk-status`,
        request
      );
      return response.data.data ?? false;
    } catch (error) {
      throw error;
    }
  }

  async bulkUpdateItemFields(
    request: BulkUpdateItemFieldsRequest
  ): Promise<boolean> {
    try {
      const response = await apiClient.put<ApiResponse<boolean>>(
        `${this.baseUrl}/bulk-update-fields`,
        request,
        withLongTimeout()
      );
      return response.data.data ?? false;
    } catch (error) {
      throw error;
    }
  }

  async addOrderItem(
    request: AddOrderItemRequest
  ): Promise<OrderItemDetail> {
    try {
      const response = await apiClient.post<ApiResponse<OrderItemDetail>>(
        `${this.baseUrl}`,
        request
      );

      if (!response.data.data) {
        throw new Error("No data received from API");
      }

      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteOrderItem(orderItemId: string): Promise<boolean> {
    try {
      const response = await apiClient.delete<ApiResponse<boolean>>(
        `${this.baseUrl}/${orderItemId}`
      );
      return response.data.data ?? false;
    } catch (error) {
      throw error;
    }
  }

  async restoreOrderItem(orderItemId: string): Promise<boolean> {
    const response = await apiClient.put<ApiResponse<boolean>>(
      `${this.baseUrl}/${orderItemId}/restore`
    );
    return response.data.data ?? false;
  }

  async getOrderItemHistory(
    orderItemId: string,
    pageNumber = 1,
    pageSize = 20
  ): Promise<PaginatedHistoryResponse> {
    try {
      const response = await apiClient.get<
        ApiResponse<PaginatedHistoryResponse>
      >(`/admin/history/entity/OrderItem/${orderItemId}`, {
        params: { pageNumber, pageSize },
      });

      if (!response.data.data) {
        throw new Error("No history data received from server");
      }

      return response.data.data;
    } catch (error) {
      console.error("Error getting order item history:", error);
      throw error;
    }
  }
}

export const adminOrderItemService: AdminOrderItemService =
  new AdminOrderItemServiceImpl();
